// ═══════════════════════════════════════════════════════════════════════
// V80 — Détection du mode de plan nutrition : one-shot vs followup
//
// Le mode est déterminé à partir de `client.packType` (déjà en base).
// Aucune nouvelle donnée, aucune migration.
//
// La classification vient exclusivement de packSystem et couvre les offres
// actives comme les offres archivées. Un pack absent ou inconnu reste fermé
// en mode ponctuel au lieu d'ouvrir un suivi par défaut.
//
// Le mode pilote :
//   - le prompt système IA (ONESHOT_PLAN_PROMPT vs FOUR_WEEKS_PROMPT)
//   - un badge discret dans l'UI
// ═══════════════════════════════════════════════════════════════════════

import { getPackAccess } from './packSystem';

/**
 * @param {{ packType?: string, pack_type?: string }} client
 * @returns {'oneshot' | 'followup' | null}
 */
export function getNutritionPlanMode(client) {
  const packType = client?.packType || client?.pack_type || '';
  return getPackAccess(packType).appMode;
}

/**
 * Label court pour affichage UI.
 */
export function planModeLabel(mode) {
  if (mode !== 'oneshot' && mode !== 'followup') return 'Pack a verifier';
  return mode === 'oneshot' ? 'Bilan individuel' : 'Suivi';
}

/**
 * Description longue (pour tooltip / aide contextuelle si besoin).
 */
export function planModeDescription(mode) {
  if (mode !== 'oneshot' && mode !== 'followup') {
    return 'Pack absent ou inconnu - generation et publication indisponibles.';
  }
  return mode === 'oneshot'
    ? 'Consultation unique — plan autonome à appliquer seul pendant 4 semaines.'
    : 'Accompagnement continu — plan évolutif avec ajustements prévus.';
}

/**
 * Calcule l'ordinal de publication d'un suivi dans la periode contractuelle
 * courante. Un historique sans date ou une horloge non prouvee ferme le
 * calcul au lieu de reutiliser les semaines du pack precedent.
 */
export function resolveFollowupWeekForPeriod({
  explicitWeek,
  isFollowup,
  consultations,
  packStartedAt,
  packStartedAtConfirmed,
  maximum = null,
} = {}) {
  if (Number.isInteger(explicitWeek) && explicitWeek >= 1 && explicitWeek <= maximum) {
    return explicitWeek;
  }
  if (!isFollowup) return 0;
  if (!Number.isInteger(maximum) || maximum < 1) return null;
  if (packStartedAtConfirmed !== true) return null;
  const startMs = Date.parse(packStartedAt || '');
  if (!Number.isFinite(startMs)) return null;
  const followups = [];
  for (const consultation of (Array.isArray(consultations) ? consultations : [])) {
    if (consultation?.isFollowup !== true && consultation?.is_followup !== true) continue;
    const createdMs = Date.parse(
      consultation?.createdAt || consultation?.created_at || consultation?.date || '',
    );
    if (!Number.isFinite(createdMs)) return null;
    if (createdMs >= startMs) followups.push(consultation);
  }
  if (followups.length >= maximum) return null;
  return followups.length + 1;
}
